// react
import * as React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

// material ui
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  ColumnsPanelTrigger,
  ExportCsv,
  ExportExcel,
  ExportPrint,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton,
} from '@mui/x-data-grid-premium';

// ─── Styled helpers ──────────────────────────────────────────────────────────

type OwnerState = { expanded: boolean };

const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

const StyledToolbarButton = styled(ToolbarButton)<{ ownerState: OwnerState }>(
  ({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    width: 'min-content',
    height: 'min-content',
    zIndex: 1,
    opacity: ownerState.expanded ? 0 : 1,
    pointerEvents: ownerState.expanded ? 'none' : 'auto',
    transition: theme.transitions.create(['opacity']),
  }),
);

// Explicit prop type to ensure styled component carries all TextFieldProps through
type StyledTextFieldProps = TextFieldProps & { ownerState: OwnerState };

const StyledTextField = styled(TextField)<StyledTextFieldProps>(
  ({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    width: ownerState.expanded ? 260 : 'var(--trigger-width)',
    opacity: ownerState.expanded ? 1 : 0,
    transition: theme.transitions.create(['width', 'opacity']),
  }),
);

// ─── Props ───────────────────────────────────────────────────────────────────

export interface DataGridToolbarProps {
  /** Optional title rendered on the left side of the toolbar. */
  title?: string;
  /** Show the column-visibility toggle button. Default: false */
  showColumns?: boolean;
  /** Show the filter panel toggle button. Default: false */
  showFilters?: boolean;

  /** Show the export (CSV / print) menu. Default: false */
  showExport?: boolean;
  /** Include Excel in the export menu. Requires showExport. Default: false */
  showExcelExport?: boolean;
  /** Show the quick-search field. Default: true */
  showQuickFilter?: boolean;
  /** Show the divider between filter/column buttons and the export button. Default: true */
  showDivider?: boolean;
  /** Render additional content at the right of the title / left of the action buttons. */
  children?: React.ReactNode;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DataGridToolbar({
  title,
  showColumns = false,
  showFilters = false,
  showExport = false,
  showExcelExport = false,
  showQuickFilter = true,
  showDivider = false,
  children,
}: DataGridToolbarProps) {
  const { t } = useTranslation('common');
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );
  const exportMenuOpen = Boolean(anchorEl);

  const openExportMenu = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const closeExportMenu = () => setAnchorEl(null);

  const hasExportOrQuickFilter = showExport || showQuickFilter;
  const hasColumnsOrFilters = showColumns || showFilters;
  const needsDivider =
    showDivider && hasColumnsOrFilters && hasExportOrQuickFilter;

  return (
    <Toolbar data-tour="datagrid-toolbar">
      {title && (
        <Typography variant="subtitle1" component="h3" noWrap sx={{ mx: 1 }}>
          {title}
        </Typography>
      )}

      {/* Quick filter (animated expand-on-click) */}
      {showQuickFilter && (
        <StyledQuickFilter>
          <QuickFilterTrigger
            render={(triggerProps, state) => (
              <Tooltip title={t('datagrid.toolbar.search')} enterDelay={0}>
                <StyledToolbarButton
                  {...triggerProps}
                  ownerState={{ expanded: state.expanded }}
                  color="default"
                  data-tour="datagrid-search-trigger"
                  aria-disabled={state.expanded}
                >
                  <SearchIcon fontSize="small" />
                </StyledToolbarButton>
              </Tooltip>
            )}
          />
          <QuickFilterControl
            render={({ ref, ...controlProps }, state) => (
              <StyledTextField
                {...(controlProps as TextFieldProps)}
                ownerState={{ expanded: state.expanded }}
                inputRef={ref}
                aria-label={t('datagrid.toolbar.search')}
                placeholder={t('datagrid.toolbar.searchPlaceholder')}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  // Always mounted so the field's height doesn't change when the
                  // clear button appears on first keystroke.
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ visibility: state.value ? 'visible' : 'hidden' }}
                    >
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label={t('datagrid.toolbar.searchClear')}
                        material={{ sx: { marginRight: -0.75, p: 0 } }}
                      >
                        <CancelIcon fontSize="small" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </StyledQuickFilter>
      )}

      {/* Spacer pushes everything after to the right */}
      <span style={{ flex: 1 }} />

      {/* Columns panel trigger */}
      {showColumns && (
        <Tooltip title={t('datagrid.toolbar.columns')}>
          <ColumnsPanelTrigger
            render={<ToolbarButton data-tour="datagrid-columns-trigger" />}
          >
            <ViewColumnIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
      )}

      {/* Export menu */}
      {showExport && (
        <>
          <Tooltip title={t('datagrid.toolbar.export')}>
            <ToolbarButton
              id="export-menu-trigger"
              data-tour="datagrid-export-trigger"
              aria-controls="export-menu"
              aria-haspopup="true"
              aria-expanded={exportMenuOpen ? 'true' : undefined}
              onClick={openExportMenu}
            >
              <FileDownloadIcon fontSize="small" />
            </ToolbarButton>
          </Tooltip>

          <Menu
            id="export-menu"
            anchorEl={anchorEl}
            open={exportMenuOpen}
            onClose={closeExportMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {showExcelExport && (
              <ExportExcel render={<MenuItem />} onClick={closeExportMenu}>
                {t('datagrid.toolbar.exportExcel')}
              </ExportExcel>
            )}
            <ExportCsv render={<MenuItem />} onClick={closeExportMenu}>
              {t('datagrid.toolbar.exportCsv')}
            </ExportCsv>
            <ExportPrint render={<MenuItem />} onClick={closeExportMenu}>
              {t('datagrid.toolbar.exportPrint')}
            </ExportPrint>
          </Menu>
        </>
      )}

      {/* Filter panel trigger */}
      {showFilters && (
        <Tooltip title={t('datagrid.toolbar.filters')}>
          <FilterPanelTrigger
            render={(props, state) => (
              <ToolbarButton
                {...props}
                color="default"
                data-tour="datagrid-filters-trigger"
              >
                <Badge
                  badgeContent={state.filterCount}
                  color="primary"
                  variant="dot"
                >
                  <FilterAltIcon fontSize="small" />
                </Badge>
              </ToolbarButton>
            )}
          />
        </Tooltip>
      )}

      {/* Divider between panel triggers and export/search */}
      {needsDivider && (
        <Divider
          orientation="vertical"
          variant="middle"
          flexItem
          sx={{ mx: 0.5 }}
        />
      )}

      {/* Middle: custom content (e.g. action buttons passed by consumer) */}
      {children && <React.Fragment>{children}</React.Fragment>}
    </Toolbar>
  );
}
